import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Activity, RefreshCw, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import ActivityRow from "../WorkspaceOverview/ActivityRow";
import ActivityModal from "./ActivityModal";
import betaAPI from "v2/api";
import useInfiniteScroll from "v2/hooks/useInfiniteScroll";

// Skeleton component for loading state
const ActivityRowSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3">
    <div className="h-7 w-7 rounded-full bg-[#E5E7EB]" />
    <div className="flex-1">
      <div className="h-3 w-48 rounded bg-[#E5E7EB]" />
    </div>
    <div className="h-2.5 w-16 rounded bg-[#E5E7EB]" />
  </div>
);

// `embedded`: render inside another card (TeamFeedCard) — no own card chrome,
// no height cap (the surrounding pane scrolls), title replaced by the count.
const TeamActivitySection = ({ className = "", embedded = false }) => {
  const { t } = useTranslation();
  const { workspaceId, teamId } = useParams();
  const [showModal, setShowModal] = useState(false);

  const fetchFn = useCallback(
    async ({ page, pageSize }) => {
      const response = await betaAPI.getTeamActivity({
        workspaceId,
        teamId,
        queryParams: { page, page_size: pageSize },
      });
      return { data: response.data?.activities || { results: [] } };
    },
    [workspaceId, teamId]
  );

  const {
    items: activities,
    loading,
    totalCount,
    error,
    lastItemRef,
    refresh,
  } = useInfiniteScroll({ fetchFn, pageSize: 20 });

  const initialLoading = loading && activities.length === 0;

  const handleRetry = () => {
    refresh();
  };

  return (
    <div
      className={`flex flex-col ${
        embedded ? "" : "rounded-xl border border-[#F3F4F6] bg-white"
      } ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-2">
          {embedded ? (
            <span className="text-xs text-[#6B7280]">
              {!initialLoading && !error
                ? t("team.activitySection.eventCount", { count: totalCount })
                : t("team.activitySection.recentActivityLower")}
            </span>
          ) : (
            <>
              <h2 className="text-sm font-semibold text-[#111827]">
                {t("team.activitySection.title")}
              </h2>
              {!initialLoading && !error && activities.length > 0 && (
                <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
                  {totalCount}
                </span>
              )}
            </>
          )}
        </div>
        {!initialLoading && !error && activities.length > 0 && (
          <button
            onClick={() => setShowModal(true)}
            className="text-xs font-medium text-[#6B7280] transition duration-150 hover:text-[#374151]"
          >
            {t("team.activitySection.viewAll")}
          </button>
        )}
      </div>

      {/* Activity list — scrolls in place, loading older entries as you reach
          the bottom. */}
      <div
        className={embedded ? "flex-1" : "max-h-[416px] flex-1 overflow-y-auto"}
      >
        {initialLoading ? (
          <>
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
          </>
        ) : error ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <Activity size={20} className="text-[#DC2626]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.activitySection.loadFailed")}
            </h3>
            <p className="mb-3 text-center text-xs text-[#6B7280]">{error}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.retry")}
            </button>
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7]">
              <Activity size={22} className="text-[#10B981]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.activitySection.emptyTitle")}
            </h3>
            <p className="max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("team.activitySection.emptyBody")}
            </p>
          </div>
        ) : (
          activities.map((activity, index) => (
            <div
              key={activity.id}
              ref={index === activities.length - 1 ? lastItemRef : undefined}
            >
              <ActivityRow
                activity={activity}
                isLast={index === activities.length - 1}
              />
            </div>
          ))
        )}
        {!initialLoading && loading && activities.length > 0 && (
          <div className="flex items-center justify-center py-3">
            <Loader2 size={16} className="animate-spin text-[#E07A3A]" />
          </div>
        )}
      </div>

      <ActivityModal show={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default TeamActivitySection;
